import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { selectSpec, selectHasEffectiveSpec } from "./limsSpecs"

// Product test plan (F-B2, SPEC-005 SP-B). Products in this app are just a `productName`
// string on samples — there is NO product entity. The backend auto-tracks a product→analysis
// mapping (product_analysis_relationship), surfaced at state.sampleView.productAnalysis. That
// mapping reflects sample HISTORY, not intent.
//
// F-B2 lets QC define an AUTHORITATIVE, curated test plan per product: the explicit set of
// analysis definitions (and thus their F-B1 specs) that apply to a product, independent of
// what has happened to land on samples. The auto-tracked relationships become *suggestions*.
// This unblocks F-C1 (auto-derive a sample's test plan) and F-C2 (result entry vs. spec).
//
// Persisted via the real /lims/productTestPlan endpoints, keyed by the product name (unique per
// team; the backend stores the plan on Product.qc_analysis_definition_ids). See
// docs/specs/lims-api-contract.md.
//
// state shape: { [productName]: { analysisDefinitionIds: ["<defId>", ...] } }
const initialState = {}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsProductTestPlan/fetch": {
      const map = {}
      action.res.forEach((item) => {
        map[item.id] = { analysisDefinitionIds: item.analysisDefinitionIds || [] }
      })
      return map
    }
    case "limsProductTestPlan/set":
      return {
        ...state,
        [action.res.id]: { analysisDefinitionIds: action.res.analysisDefinitionIds || [] },
      }
    case "limsProductTestPlan/remove": {
      const next = { ...state }
      delete next[action.res.id]
      return next
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsProductTestPlan/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/productTestPlan").then((res) => res.items)
})

// Upsert the curated plan for one product. The product name is the record id, so reads map
// straight back onto the product. The optional `reason` rides at the request-body TOP LEVEL so
// the server's limsAuditProductPlanCreated/-Updated audit entry carries it (SPEC-021 m2; the
// server itself decides created-vs-updated from the prior plan state).
export const setForProduct = asyncAction(
  "limsProductTestPlan/set",
  (_dispatch, productName, analysisDefinitionIds, reason = null) => {
    const record = {
      id: productName,
      productName,
      analysisDefinitionIds: analysisDefinitionIds || [],
    }
    return limsRequest("POST", "/lims/productTestPlan", {
      input: record,
      reason: reason || undefined,
    }).then(() => record)
  }
)

// Remove the curated plan for a product. The auto-tracked suggestions are untouched (they
// live in sampleView), so the product simply reverts to "suggestions only". The optional
// `reason` rides top-level for the server's limsAuditProductPlanDeleted entry.
export const removeForProduct = asyncAction(
  "limsProductTestPlan/remove",
  (_dispatch, productName, reason = null) => {
    return limsRequest(
      "DELETE",
      `/lims/productTestPlan/${encodeURIComponent(productName)}`,
      reason ? { reason } : undefined
    ).then(() => ({ id: productName }))
  }
)

// Build a name→definition lookup from the analysisDefinitions map (keyed by id). The
// product_analysis_relationship suggestions resolve by NAME (the backend has no id there yet),
// so we match on the definition's current name. SP-E should add analysis_definition_id to the
// relationship to make this a stable id join — see lims-api-contract.md.
function definitionsByName(state) {
  const byName = {}
  Object.values(state.analysisDefinitions || {}).forEach((def) => {
    if (def && def.name && byName[def.name] === undefined) {
      byName[def.name] = def
    }
  })
  return byName
}

// The raw curated plan for one product (or an empty plan if none stored).
export const selectProductPlan = (state, productName) =>
  state.limsProductTestPlan[productName] || { analysisDefinitionIds: [] }

// Product names that have a curated plan stored.
export const selectPlannedProductNames = createSelector(
  [(state) => state.limsProductTestPlan],
  (plan) => Object.keys(plan)
)

// The COMBINED view for the UI + downstream (F-C1):
//   curated:     resolved from the curated ids (+ each one's F-B1 spec)
//   suggestions: from sampleView.productAnalysis, EXCLUDING any analysis already curated
// A curated defId resolves to its definition's current name; an unmatched suggestion name
// keeps analysisDefinitionId: null (so the UI can disable "add to plan" for it).
export const selectProductTestPlan = (state, productName) => {
  const defsById = state.analysisDefinitions || {}
  const byName = definitionsByName(state)
  const plan = state.limsProductTestPlan[productName] || { analysisDefinitionIds: [] }
  const curatedIds = plan.analysisDefinitionIds || []

  const curated = curatedIds.map((defId) => {
    const def = defsById[defId]
    const spec = selectSpec(state, defId)
    return {
      analysisDefinitionId: defId,
      analysisName: def ? def.name : defId,
      hasSpec: selectHasEffectiveSpec(state, defId),
      spec: spec || null,
    }
  })

  // Names already curated (resolved to current definition names) — excluded from suggestions.
  const curatedNames = new Set(curated.map((c) => c.analysisName))

  const relationships = (state.sampleView && state.sampleView.productAnalysis
    && state.sampleView.productAnalysis[productName]) || []

  const suggestions = relationships
    .map((rel) => {
      const analysisName = rel.analysis_name
      const def = analysisName ? byName[analysisName] : undefined
      return {
        analysisName,
        analysisDefinitionId: def ? def.id : null,
        occurrenceCount: rel.occurrence_count != null ? rel.occurrence_count : null,
        lastSeen: rel.last_seen || null,
        relationshipId: rel.id,
      }
    })
    .filter((s) => s.analysisName && !curatedNames.has(s.analysisName))

  return { curated, suggestions }
}
